import {
  useCallback,
  useRef,
  Dispatch,
  SetStateAction,
  MutableRefObject,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import uuid from 'react-uuid';
import t from 'react-translate';

import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import {
  startRolePlaySession,
  StartSessionPayload,
  setAiRolePlaySubmissionClosingReason,
} from 'redux/actions/ai-role-play';
import { wrapThunkAction } from 'redux/utils';
import { danger } from 'styles/global_defaults/colors';

import {
  AIRolePlayStatus,
  Message,
  MessageStatus,
  Role,
  SessionData,
} from '../ai-conversation-modal/context';
import streamAIResponse, { ClosingReason } from '../ai-conversation-modal/stream-ai-response';

interface UseAiConversationHandlersParams {
  aiRolePlayId: number;
  setAiRolePlayStatus: Dispatch<SetStateAction<AIRolePlayStatus>>;
  setShowScenarioDetails: Dispatch<SetStateAction<boolean>>;
  messages: Message[];
  setMessages: Dispatch<SetStateAction<Message[]>>;
  sessionData: SessionData;
  setSessionData: Dispatch<SetStateAction<SessionData>>;
  setIsTimeout: Dispatch<SetStateAction<boolean>>;
  abortControllerRef: MutableRefObject<AbortController | null>;
  setIsRolePlayStarting: Dispatch<SetStateAction<boolean>>;
  openingNarrative: string;
  setOpeningNarrative: Dispatch<SetStateAction<string>>;
}

interface UseAiConversationHandlersReturn {
  openingNarrative: string;
  handleChunk: (chunk: any) => void;
  handleError: () => void;
  handleStartConversation: () => void;
}

const useAiConversationHandlers = ({
  aiRolePlayId,
  setAiRolePlayStatus,
  setShowScenarioDetails,
  setMessages,
  setSessionData,
  sessionData,
  setIsTimeout,
  abortControllerRef,
  setIsRolePlayStarting,
  openingNarrative,
  setOpeningNarrative,
}: UseAiConversationHandlersParams): UseAiConversationHandlersReturn => {
  const dispatch = useDispatch();
  const currentCatalogId = useSelector((state) => state.app.currentCatalogId);

  const hasReceivedNarrativeRef = useRef(false);
  // Use a ref to track submissionId to avoid stale closure in handleChunk
  // when it's passed to streamAIResponse before sessionData is updated
  const submissionIdRef = useRef<number | null>(sessionData.submissionId);
  submissionIdRef.current = sessionData.submissionId;

  const handleError = useCallback(() => {
    dispatch(addAlertMessage({
      type: AlertMessageType.WARNING,
      header: t.FORM.OOPS(),
      message: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SOMETHING_WENT_WRONG(),
      customColor: danger,
    }));
    setMessages((prevMessages) => {
      const lastMessage = prevMessages[prevMessages.length - 1];
      if (lastMessage) {
        const updatedMessage = {
          ...lastMessage,
          status: MessageStatus.FAILED,
        };
        return [...prevMessages.slice(0, -1), updatedMessage];
      }
      return prevMessages;
    });
  }, [dispatch, setMessages]);

  // To pass as callback to streamAIResponse for handling incoming response chunks and updating the conversation in real-time.
  const handleChunk = useCallback((chunk) => {
    // set initial message as opening narrative
    if (!hasReceivedNarrativeRef.current) {
      setOpeningNarrative((prev) => prev + chunk.content);
      if (chunk.isLastChunk) {
        hasReceivedNarrativeRef.current = true;
      }
    } else {
      setMessages((prevMessages) => {
        const lastMessage = prevMessages[prevMessages.length - 1];
        if (lastMessage && lastMessage.role === Role.AI && lastMessage.status === MessageStatus.PENDING) {
          // Update the pending AI message with new content.
          const updatedMessage = {
            ...lastMessage,
            content: lastMessage.content + chunk.content,
            status: chunk.isLastChunk ? MessageStatus.SENT : MessageStatus.PENDING,
          };
          return [...prevMessages.slice(0, -1), updatedMessage];
        }
        // Mark the learner message as SENT now that AI is responding, then add AI message
        const updatedMessages = prevMessages.map((msg) => {
          if (msg.role === Role.LEARNER && msg.status === MessageStatus.PENDING) {
            return { ...msg, status: MessageStatus.SENT };
          }
          return msg;
        });
        // Add a new AI message.
        return [...updatedMessages,
          {
            id: uuid(),
            content: chunk.content,
            role: Role.AI,
            status: chunk.isLastChunk ? MessageStatus.SENT : MessageStatus.PENDING,
          }];
      });
    }

    if (chunk.closingReason === ClosingReason.COMPLETED
        || chunk.closingReason === ClosingReason.TERMINATED) {
      if (submissionIdRef.current) {
        dispatch(setAiRolePlaySubmissionClosingReason({
          aiRolePlaySubmissionId: submissionIdRef.current,
          closingReason: chunk.closingReason,
          aiRolePlayId,
        }));
      }
      setAiRolePlayStatus(AIRolePlayStatus.COMPLETED);
    }
  }, [
    setOpeningNarrative,
    setMessages,
    setAiRolePlayStatus,
    dispatch,
    aiRolePlayId,
  ]);

  const handleStartConversation = useCallback(() => {
    setIsRolePlayStarting(true);

    // Abort any existing stream before starting a new one
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    wrapThunkAction(
      dispatch(startRolePlaySession({
        catalogId: currentCatalogId,
        aiRolePlayId,
      })),
    ).then((res) => {
      const sessionPayload = res.payload as StartSessionPayload;

      if (!sessionPayload?.jwtToken || !sessionPayload?.aiApiEndpoint) {
        handleError();
        return;
      }

      const normalizedAiApiEndpoint = sessionPayload.aiApiEndpoint.replace(/\/+$/, '');
      setSessionData({
        aiApiEndpoint: normalizedAiApiEndpoint,
        jwtToken: sessionPayload.jwtToken,
        submissionId: sessionPayload.submissionId || null,
        expiresAt: sessionPayload.expiresAt || null,
      });
      // Update ref immediately so handleChunk has the correct submissionId
      // before setSessionData triggers a re-render
      submissionIdRef.current = sessionPayload.submissionId || null;
      setMessages([]);
      setOpeningNarrative('');
      hasReceivedNarrativeRef.current = false;
      setAiRolePlayStatus(AIRolePlayStatus.IN_PROGRESS);
      setShowScenarioDetails(false);
      setIsTimeout(false);

      streamAIResponse({
        aiApiEndpoint: sessionPayload.aiApiEndpoint,
        token: sessionPayload.jwtToken,
        userMessage: null,
        onChunk: handleChunk,
        onError: handleError,
        abortSignal: abortControllerRef.current?.signal,
      }).catch((_error) => {
        // handleError was already called by onError callback, just reset state
        setAiRolePlayStatus(AIRolePlayStatus.NOT_STARTED);
        setOpeningNarrative('');
        hasReceivedNarrativeRef.current = false;
        setSessionData({ aiApiEndpoint: null, jwtToken: null, submissionId: null, expiresAt: null });
      });
    }).catch(() => {
      setAiRolePlayStatus(AIRolePlayStatus.NOT_STARTED);
      setSessionData({ aiApiEndpoint: null, jwtToken: null, submissionId: null, expiresAt: null });
      handleError();
    }).finally(() => {
      setIsRolePlayStarting(false);
    });
  }, [
    dispatch,
    currentCatalogId,
    aiRolePlayId,
    setShowScenarioDetails,
    setAiRolePlayStatus,
    setSessionData,
    handleChunk,
    handleError,
    setMessages,
    setIsTimeout,
    abortControllerRef,
    setIsRolePlayStarting,
    setOpeningNarrative,
  ]);

  return {
    openingNarrative,
    handleChunk,
    handleError,
    handleStartConversation,
  };
};

export default useAiConversationHandlers;
